/**
 * TextField -- Dynamic text input field.
 *
 * Renders a standard text input backed by the shadcn/ui Input component.
 * Supports label, required indicator, placeholder, help text, tooltip,
 * validation error display, and full ARIA accessibility.
 */

import { HelpCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { DynamicFieldProps } from './index';

export function TextField({ field, value, onChange, error, readOnly }: DynamicFieldProps) {
  const inputId = `field-${field.name}`;
  const helpId = `${inputId}-help`;
  const errorId = `${inputId}-error`;

  const describedBy = [
    field.help_text ? helpId : null,
    error ? errorId : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <Label htmlFor={inputId}>
          {field.label}
          {field.required && (
            <>
              <span aria-hidden="true" className="text-destructive ml-1">*</span>
              <span className="sr-only">(required)</span>
            </>
          )}
        </Label>

        {field.tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle
                  className="h-3.5 w-3.5 text-muted-foreground cursor-help"
                  aria-hidden="true"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-sm">{field.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <Input
        id={inputId}
        type="text"
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder ?? undefined}
        disabled={readOnly}
        maxLength={field.max_length ?? undefined}
        aria-required={field.required}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        className={cn(error && 'border-destructive focus-visible:ring-destructive')}
      />

      {field.help_text && (
        <p id={helpId} className="text-sm text-muted-foreground">
          {field.help_text}
        </p>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
